import { useQuery } from '@tanstack/react-query';
import { medicalProfileFilesService } from '../../primary-record-types/apiTypes';
import { getIntl } from '../../utils/intl';
import { getMedicalProfileFilePageImageCacheKey } from './cacheKeys';
import { appAPI } from '../../utils/api';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import _uniqBy from 'lodash/uniqBy';
import { useEffect } from 'react';

const intl = getIntl('useMedicalProfileFileImage', {
    notFound: 'Error loading page image',
});

export type UseMedicalProfileFileImageArgs = {
    medicalProfileId: string;
    medicalProfileFileId: string;
    page: number;
};

export type NoImageResponse = {
    status: 'loading' | 'not-found';
    image: null;
};

export type ImageResponse = {
    status: 'loaded';
    image: medicalProfileFilesService.ImageDetails;
};

export type UseMedicalProfileFileImageResponse = NoImageResponse | ImageResponse;

// keeps track of refetch state for each medical profile file image.  Stored in global object to prevent multiple hook instances refetching the same page
const globalRefetchState: Record<string, boolean> = {};
const getRefetchStateKey = (medicalProfileId: string, medicalProfileFileId: string, page: number) =>
    `${medicalProfileId}-${medicalProfileFileId}-${page}`;

export const useMedicalProfileFileImage = ({
    medicalProfileId,
    medicalProfileFileId,
    page,
}: UseMedicalProfileFileImageArgs): UseMedicalProfileFileImageResponse => {
    const searchQuery = useQuery({
        queryKey: getMedicalProfileFilePageImageCacheKey(medicalProfileId, medicalProfileFileId, page),
        queryFn: async () => {
            try {
                const response = await appAPI.get<APITypes.medicalProfileFilesService.GetPageImageResponse>(
                    urls.api.medicalProfileFiles.pageImage(medicalProfileId, medicalProfileFileId, page),
                );
                return response;
            } catch (error) {
                throw error;
            } finally {
                globalRefetchState[getRefetchStateKey(medicalProfileId, medicalProfileFileId, page)] = false;
            }
        },
        staleTime: 1000 * 60 * 30, // 30 minutes
    });
    useQueryErrorNotify(searchQuery, intl('notFound'));

    // refetch if stale.  this does not happen automatically
    useEffect(() => {
        if (
            searchQuery.isStale &&
            !searchQuery.isFetching &&
            // only refetch if not already refetching from another hook for this page
            !globalRefetchState[getRefetchStateKey(medicalProfileId, medicalProfileFileId, page)]
        ) {
            globalRefetchState[getRefetchStateKey(medicalProfileId, medicalProfileFileId, page)] = true;
            searchQuery.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery.isStale]);

    if (searchQuery.isLoading || searchQuery.isStale || searchQuery.isFetching) {
        return { status: 'loading', image: null };
    } else if (searchQuery.isError || !searchQuery.data?.imageDetails) {
        return { status: 'not-found', image: null };
    } else {
        return { status: 'loaded', image: searchQuery.data.imageDetails };
    }
};

export type UseMedicalProfileBufferedFileImageArgs = UseMedicalProfileFileImageArgs & {
    pageCount: number;
};

type GetBufferPageArgs = {
    page: number;
    pageCount: number;
    offset: number;
};
const getBufferPage = ({ page, pageCount, offset }: GetBufferPageArgs): number => {
    const unValidatedPage = page + offset;
    if (unValidatedPage < 1) return 1;
    if (unValidatedPage > pageCount) return pageCount;

    return unValidatedPage;
};

export const BUFFER_SIZE = 3;

const getBufferedPages = ({ page, pageCount }: Omit<GetBufferPageArgs, 'offset'>): number[] => {
    // put active page first to prioritize loading
    const returnPages = [page];
    for (let i = 1; i <= BUFFER_SIZE; i++) {
        returnPages.push(getBufferPage({ page, pageCount, offset: i }));
        returnPages.push(getBufferPage({ page, pageCount, offset: -i }));
    }

    return returnPages;
};

export type BufferedPage = {
    page: number;
    image: UseMedicalProfileFileImageResponse;
};

export type UseMedicalProfileBufferedFileImageResponse = {
    activePageImage: BufferedPage;
    bufferedPages: BufferedPage[];
};

export const useMedicalProfileBufferedFileImage = ({
    medicalProfileId,
    medicalProfileFileId,
    page,
    pageCount,
}: UseMedicalProfileBufferedFileImageArgs): UseMedicalProfileBufferedFileImageResponse => {
    const [activePage, ...rest] = getBufferedPages({ page, pageCount }).map((page) => {
        // calling in a loop is generally bad, but the array length is deterministic here
        return {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            image: useMedicalProfileFileImage({ medicalProfileId, medicalProfileFileId, page }),
            page,
        };
    });

    const uniqueBufferedPages = _uniqBy(
        rest.filter((_) => _.page !== activePage.page),
        'page',
    );

    return {
        activePageImage: activePage,
        bufferedPages: uniqueBufferedPages,
    };
};
